import { Node, Edge } from "reactflow";
import dagre from "dagre";
import { OrganogramRecord, Employee } from "../../../../../models/DSF/OrganogramAndAppointments";

interface TransformResult {
  nodes: Node[];
  edges: Edge[];
  employees: (Employee & { parentId?: string; parentName?: string })[];
}

// Dynamic height calculation function
function calculateNodeHeight(nodeData: any, isCompact: boolean): number {
  // Base height for header (position name + legal reference)
  let height = 80; // Header + padding

  // Add height for legal reference if present
  if (nodeData?.legalReference && nodeData.legalReference.trim()) {
    height += 25; // Legal reference line
  }

  // Add height for employees section
  const employees = nodeData?.employees || [];
  const isExpanded = nodeData?.isExpanded !== false;

  if (employees.length > 0) {
    height += 35; // Employees header with toggle button

    if (isExpanded) {
      // Calculate height based on actual employee count
      const employeeHeight = 35; // Height per employee (name + title)
      height += employees.length * employeeHeight;

      // Add some padding for the employees list
      height += 15;
    } else {
      // Collapsed view, just show the count
      height += 20;
    }
  } else {
    // No employees section
    height += 25;
  }

  // Add bottom padding
  height += 15;

  // Apply minimum constraints
  const minHeight = isCompact ? 120 : 140;
  return Math.max(height, minHeight);
}

// Layout configuration
const LAYOUT_CONFIG = {
  compact: {
    nodeWidth: 280,
    nodeHeight: 160, // This will be overridden by dynamic calculation
    verticalSpacing: 140,
    horizontalSpacing: 50,
  },
  expanded: {
    nodeWidth: 320,
    nodeHeight: 220, // This will be overridden by dynamic calculation
    verticalSpacing: 180,
    horizontalSpacing: 80,
  },
};

// Dagre layout function to automatically position nodes
export function applyDagreLayout(
  nodes: Node[],
  edges: Edge[],
  direction: "TB" | "LR" = "TB",
  isCompact: boolean = false
): { nodes: Node[]; edges: Edge[] } {
  const layout = isCompact ? LAYOUT_CONFIG.compact : LAYOUT_CONFIG.expanded;

  // Create a new dagre graph
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));

  // Set graph layout direction and spacing
  g.setGraph({
    rankdir: direction,
    nodesep: layout.horizontalSpacing,
    ranksep: layout.verticalSpacing,
    marginx: 20,
    marginy: 20,
  });

  // Add nodes to dagre graph with dynamic heights
  nodes.forEach(node => {
    // Calculate dynamic height based on node content
    const nodeData = node.data as any;
    const dynamicHeight = calculateNodeHeight(nodeData, isCompact);

    // Debug logging for height calculation
    if (nodeData && nodeData.employees && nodeData.employees.length > 0) {
      console.log(
        `Node ${node.id}: employees=${nodeData.employees.length}, isExpanded=${nodeData.isExpanded}, dynamicHeight=${dynamicHeight}px`
      );
    }

    g.setNode(node.id, {
      width: layout.nodeWidth,
      height: dynamicHeight,
    });
  });

  // Add edges to dagre graph
  edges.forEach(edge => {
    g.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(g);

  // Apply the calculated positions to the nodes
  const layoutedNodes = nodes.map(node => {
    const nodeWithPosition = g.node(node.id);

    // Get the dynamic height used for this node
    const dynamicHeight = nodeWithPosition.height;

    // dagre returns the center of the node, but we need the top-left corner
    const x = nodeWithPosition.x - layout.nodeWidth / 2;
    const y = nodeWithPosition.y - dynamicHeight / 2;

    return {
      ...node,
      position: { x, y },
      // Store the calculated height in the node data for reference
      data: {
        ...node.data,
        calculatedHeight: dynamicHeight,
      },
    };
  });

  return {
    nodes: layoutedNodes,
    edges,
  };
}

export function transformToReactFlowData(
  data: OrganogramRecord,
  isCompact: boolean = false,
  layoutDirection: "TB" | "LR" = "TB",
  expandedStates?: Map<string, boolean>,
  onToggleExpanded?: (nodeId: string) => void
): TransformResult {
  console.log("🔍 Debug: Starting transformation with data:", data);
  console.log("🔍 Debug: isCompact mode:", isCompact);

  // Special debug for the Assistant to Chief Executive Officer node
  const debugTargetNodeId = "dbe3c922-5405-11ef-bb4f-02393f980435";

  function findNodeRecursively(node: OrganogramRecord): OrganogramRecord | null {
    if (node.id === debugTargetNodeId) {
      return node;
    }
    if (node.child) {
      for (const child of node.child) {
        const found = findNodeRecursively(child);
        if (found) return found;
      }
    }
    return null;
  }

  const targetNode = findNodeRecursively(data);
  if (targetNode) {
    console.log("🎯 DEBUG TARGET NODE FOUND:", {
      id: targetNode.id,
      name: targetNode.name,
      employees: targetNode.employee,
      employeeCount: targetNode.employee ? targetNode.employee.length : 0,
      employeeDetails: targetNode.employee
        ? targetNode.employee.map(emp => ({
            id: emp.id,
            name: emp.name,
            surname: emp.surname,
            title: emp.title,
          }))
        : [],
    });
  } else {
    console.log("🚨 DEBUG TARGET NODE NOT FOUND!");
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const allEmployees: (Employee & { parentId?: string; parentName?: string })[] = [];

  // Recursively transform data
  function transformNode(
    node: OrganogramRecord,
    parentId?: string,
    currentExpandedStates: Map<string, boolean> = new Map(),
    isRoot: boolean = false
  ): { included: boolean; promotedNodes?: Node[] } {
    // Check if this node should be included
    const hasEmployees = node.employee && node.employee.length > 0;
    let hasValidChildren = false;
    const promotedNodes: Node[] = [];

    // CRITICAL DEBUG for target node
    if (node.id === "dbe3c922-5405-11ef-bb4f-02393f980435") {
      console.log("🚨 CRITICAL - REAL APP PROCESSING TARGET NODE:");
      console.log("- Node name:", node.name);
      console.log("- Raw employees:", node.employee);
      console.log("- Employee count:", node.employee ? node.employee.length : 0);
      console.log("- hasEmployees:", hasEmployees);
    }

    // First, check children to see if any should be included
    if (node.child && node.child.length > 0) {
      node.child.forEach(child => {
        const result = transformNode(child, node.id, currentExpandedStates, false);
        if (result.included) {
          hasValidChildren = true;
        }
        // Collect any promoted nodes from children
        if (result.promotedNodes) {
          promotedNodes.push(...result.promotedNodes);
        }
      });
    }

    // For root level (company), always include regardless of employees
    // For other levels, only include if has employees OR has valid children
    const shouldInclude = isRoot || hasEmployees || hasValidChildren;

    if (!shouldInclude) {
      // If this node is not included but has children, promote those children
      if (node.child && node.child.length > 0) {
        const childPromotions: Node[] = [];
        node.child.forEach(child => {
          const result = transformNode(child, parentId, currentExpandedStates, false); // Use parent's parentId
          if (result.promotedNodes) {
            childPromotions.push(...result.promotedNodes);
          }
        });
        return { included: false, promotedNodes: childPromotions };
      }
      return { included: false }; // Don't include this node and no promotions
    }

    const position = { x: 0, y: 0 }; // Initial position, will be updated by dagre layout
    const isExpanded = currentExpandedStates.get(node.id) ?? true;

    // Add employees to the global list
    if (hasEmployees) {
      node.employee.forEach(emp => {
        allEmployees.push({
          ...emp,
          parentId: node.id,
          parentName: node.name,
        });
      });
    }

    // Create the department/role node
    const nodeData = {
      label: node.name,
      title: undefined, // OrganogramRecord doesn't have title, only employees do
      accessLevel: node.access_level,
      legalReference: node.legal_reference,
      employees: node.employee || [],
      isEmployee: false,
      isExpanded,
      onToggleExpanded: onToggleExpanded
        ? () => onToggleExpanded(node.id)
        : () => {
            // Fallback if no callback provided
            currentExpandedStates.set(node.id, !isExpanded);
          },
    };

    // CRITICAL DEBUG for target node - AFTER creating nodeData
    if (node.id === "dbe3c922-5405-11ef-bb4f-02393f980435") {
      console.log("🚨 CRITICAL - NODE DATA CREATED:");
      console.log("- nodeData.employees:", nodeData.employees);
      console.log("- nodeData.employees length:", nodeData.employees.length);
      console.log("- Reference check - same object?:", node.employee === nodeData.employees);
    }

    // Special debug logging for the target node
    if (node.id === "dbe3c922-5405-11ef-bb4f-02393f980435") {
      console.log("🎯 CREATING TARGET NODE DATA:", {
        nodeId: node.id,
        nodeName: node.name,
        rawEmployees: node.employee,
        nodeDataEmployees: nodeData.employees,
        employeeCount: nodeData.employees.length,
        hasEmployees,
        hasValidChildren,
        isRoot,
        shouldInclude,
        employeeDetails: nodeData.employees.map(emp => ({
          id: emp.id,
          name: emp.name,
          surname: emp.surname,
          title: emp.title,
        })),
      });
    }

    const newNode: Node = {
      id: node.id,
      type: "organogram",
      position: { x: position.x, y: position.y },
      data: nodeData,
      draggable: true,
      selectable: true,
    };

    nodes.push(newNode);

    // CRITICAL DEBUG for target node - FINAL NODE
    if (node.id === "dbe3c922-5405-11ef-bb4f-02393f980435") {
      console.log("🚨 CRITICAL - FINAL NODE CREATED AND ADDED:");
      console.log("- newNode.data.employees:", newNode.data.employees);
      console.log(
        "- newNode.data.employees length:",
        newNode.data.employees ? newNode.data.employees.length : 0
      );
      console.log("- Node pushed to array at index:", nodes.length - 1);
    }

    // Add any promoted nodes from children that weren't included
    if (promotedNodes.length > 0) {
      nodes.push(...promotedNodes);
      // Create edges from this node to promoted nodes
      promotedNodes.forEach(promotedNode => {
        edges.push({
          id: `${node.id}-${promotedNode.id}`,
          source: node.id,
          target: promotedNode.id,
          type: "smoothstep",
          animated: false,
          style: {
            stroke: "#94a3b8",
            strokeWidth: 2,
          },
        });
      });
    }

    // Create edge from parent to this node
    if (parentId) {
      edges.push({
        id: `${parentId}-${node.id}`,
        source: parentId,
        target: node.id,
        type: "smoothstep",
        animated: false,
        style: {
          stroke: "#94a3b8",
          strokeWidth: 2,
        },
      });
    }

    return { included: true }; // Node was included
  }

  transformNode(data, undefined, expandedStates || new Map(), true); // Mark root as true

  // Apply dagre layout to automatically position nodes
  const { nodes: layoutedNodes, edges: layoutedEdges } = applyDagreLayout(
    nodes,
    edges,
    layoutDirection,
    isCompact
  );

  console.log("🔍 Debug: Final layouted nodes:", layoutedNodes.length);
  console.log("🔍 Debug: Final layouted edges:", layoutedEdges.length);

  return {
    nodes: layoutedNodes,
    edges: layoutedEdges,
    employees: allEmployees,
  };
}

// Utility function to search within the organogram data
export function searchOrganogram(nodes: Node[], searchTerm: string): Node[] {
  const term = searchTerm.toLowerCase();

  return nodes.filter(node => {
    const data = node.data;

    // Search in main node data
    if (
      data.label?.toLowerCase().includes(term) ||
      data.title?.toLowerCase().includes(term) ||
      data.legalReference?.toLowerCase().includes(term)
    ) {
      return true;
    }

    // Search in employees
    if (data.employees && data.employees.length > 0) {
      return data.employees.some(
        (emp: Employee) =>
          `${emp.name} ${emp.surname}`.toLowerCase().includes(term) ||
          emp.title?.toLowerCase().includes(term)
      );
    }

    return false;
  });
}

// Utility function to find the path to a specific node
export function findNodePath(nodes: Node[], edges: Edge[], targetNodeId: string): string[] {
  const path: string[] = [];
  const visited = new Set<string>();

  function dfs(nodeId: string): boolean {
    if (visited.has(nodeId)) return false;
    visited.add(nodeId);
    path.push(nodeId);

    if (nodeId === targetNodeId) return true;

    // Find children
    const childEdges = edges.filter(edge => edge.source === nodeId);
    for (const edge of childEdges) {
      if (dfs(edge.target)) return true;
    }

    path.pop();
    return false;
  }

  // Start from root nodes (nodes with no incoming edges)
  const rootNodes = nodes.filter(node => !edges.some(edge => edge.target === node.id));

  for (const rootNode of rootNodes) {
    if (dfs(rootNode.id)) break;
  }

  return path;
}

// Utility function to calculate optimal layout
export function calculateOptimalLayout(
  nodeCount: number,
  containerWidth: number,
  containerHeight: number,
  isCompact: boolean = false
): { nodeWidth: number; nodeHeight: number; spacing: number } {
  const layout = isCompact ? LAYOUT_CONFIG.compact : LAYOUT_CONFIG.expanded;

  // Calculate optimal spacing based on container size and node count
  const optimalHorizontalSpacing = Math.max(
    layout.horizontalSpacing,
    (containerWidth - layout.nodeWidth * Math.sqrt(nodeCount)) / Math.sqrt(nodeCount)
  );

  return {
    nodeWidth: layout.nodeWidth,
    nodeHeight: layout.nodeHeight,
    spacing: Math.min(optimalHorizontalSpacing, layout.horizontalSpacing * 2),
  };
}
